import { useEffect } from 'react';
import { CREDITS } from '../credits';
import { APP_VERSION } from '../diagnostics';
import { type MessageKey, useT } from '../i18n';
import { LEGAL, type LegalDoc, OPEN_SOURCE, PRIVACY, TERMS } from '../legal';
import { type LegalTab, useGame } from '../store';
import { Emblem, Icon, type IconName } from './icons';
import { LanguageSwitch } from './LanguageSwitch';

const TABS: readonly { id: LegalTab; icon: IconName; label: MessageKey }[] = [
  { id: 'about', icon: 'info', label: 'legal.about' },
  { id: 'privacy', icon: 'lock', label: 'legal.privacy' },
  { id: 'terms', icon: 'doc', label: 'legal.terms' },
  { id: 'licences', icon: 'link', label: 'legal.licences' },
];

/**
 * About the game and its legal texts, over whatever is on screen (the title screen or the
 * game): who makes it and how to reach them, the privacy policy, the terms of use and the
 * licences of what it is built with.
 */
export function LegalWindow() {
  const t = useT();
  const tab = useGame((s) => s.legal);
  const close = () => useGame.setState({ legal: null });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useGame.setState({ legal: null });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  if (!tab) return null;
  return (
    <div className="legal-backdrop">
      <section className="legal-window panel" role="dialog" aria-label={t('legal.title')}>
        <header className="legal-head">
          <span className="window-icon">
            <Icon name="info" size={20} />
          </span>
          <h2>{t('legal.title')}</h2>
          <button type="button" className="icon-btn" title={t('hud.close')} onClick={close}>
            <Icon name="close" size={18} />
          </button>
        </header>
        <nav className="legal-tabs">
          {TABS.map((it) => (
            <button
              type="button"
              key={it.id}
              className={tab === it.id ? 'active' : ''}
              aria-pressed={tab === it.id}
              onClick={() => useGame.setState({ legal: it.id })}
            >
              <Icon name={it.icon} size={15} />
              {t(it.label)}
            </button>
          ))}
        </nav>
        <div className="legal-body" key={tab}>
          {tab === 'about' && <About />}
          {tab === 'privacy' && <Doc doc={PRIVACY} />}
          {tab === 'terms' && <Doc doc={TERMS} />}
          {tab === 'licences' && <Licences />}
        </div>
      </section>
    </div>
  );
}

/** The game, its version, who makes it and how to reach them. */
function About() {
  const t = useT();
  const lang = useGame((s) => s.lang);
  return (
    <div className="legal-about">
      <div className="legal-brand">
        <Emblem size={56} />
        <div>
          <strong>{LEGAL.game}</strong>
          <span className="muted">{t('legal.version', { version: APP_VERSION })}</span>
        </div>
      </div>
      <dl className="legal-facts">
        <dt>{t('legal.publisher')}</dt>
        <dd>{LEGAL.publisher[lang]}</dd>
        <dt>{t('legal.contact')}</dt>
        <dd>
          <a href={`mailto:${LEGAL.contact}`}>{LEGAL.contact}</a>
        </dd>
        <dt>{t('legal.website')}</dt>
        <dd>
          <a href={LEGAL.site} target="_blank" rel="noreferrer">
            {LEGAL.site.replace('https://', '')}
          </a>
        </dd>
      </dl>
      <p className="muted small">{t('legal.aboutText')}</p>
      <div className="legal-links">
        {TABS.filter((it) => it.id !== 'about').map((it) => (
          <button
            type="button"
            key={it.id}
            className="btn"
            onClick={() => useGame.setState({ legal: it.id })}
          >
            <Icon name={it.icon} size={16} />
            {t(it.label)}
          </button>
        ))}
      </div>
      <p className="muted small">
        © {LEGAL.updated.slice(0, 4)} {LEGAL.publisher[lang]}
      </p>
    </div>
  );
}

/** A legal text: its title, date and sections. */
export function Doc({ doc }: { doc: LegalDoc }) {
  const t = useT();
  const lang = useGame((s) => s.lang);
  return (
    <article className="legal-doc">
      <h3>{doc.title[lang]}</h3>
      <p className="muted small">{t('legal.updated', { date: LEGAL.updated })}</p>
      {doc.blocks.map((b, i) =>
        'h' in b ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed text, never reordered
          <h4 key={i}>{b.h[lang]}</h4>
        ) : 'p' in b ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed text, never reordered
          <p key={i}>{b.p[lang]}</p>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed text, never reordered
          <ul key={i}>
            {b.list.map((it) => (
              <li key={it.en}>{it[lang]}</li>
            ))}
          </ul>
        ),
      )}
    </article>
  );
}

/** The 3D models made by others (with their licences) and the open-source software. */
function Licences() {
  const t = useT();
  return (
    <div className="legal-doc">
      <h3>{t('legal.models')}</h3>
      <p className="muted small">{t('legal.modelsText')}</p>
      <ul className="legal-credits">
        {CREDITS.map((c) => (
          <li key={c.url}>
            <a href={c.url} target="_blank" rel="noreferrer">
              {c.title}
            </a>
            <span className="muted">
              {' '}
              · {c.author} · {c.license}
            </span>
          </li>
        ))}
      </ul>
      <h3>{t('legal.software')}</h3>
      <ul className="legal-credits">
        {OPEN_SOURCE.map((s) => (
          <li key={s.name}>
            <a href={s.url} target="_blank" rel="noreferrer">
              {s.name}
            </a>
            <span className="muted"> · {s.license}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A legal text as a page of its own (/privacy, /terms: the stores link to it), with the
 * language to read it in.
 */
export function LegalPage({ doc }: { doc: 'privacy' | 'terms' }) {
  const t = useT();
  return (
    <main className="legal-page">
      <header className="legal-page-head">
        <Emblem size={40} />
        <strong>{LEGAL.game}</strong>
        <LanguageSwitch />
      </header>
      <Doc doc={doc === 'privacy' ? PRIVACY : TERMS} />
      <footer className="muted small">
        {t('legal.contact')}: <a href={`mailto:${LEGAL.contact}`}>{LEGAL.contact}</a>
      </footer>
    </main>
  );
}
